import React, { useState, useEffect, useRef } from 'react';
import {
    LifeBuoy, Plus, Send, MessageSquare, Clock, CheckCircle2,
    AlertCircle, ChevronRight, Search,
    HelpCircle, Bug, Lightbulb, CreditCard, X, Loader2
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../supabase/config';

const CATEGORIES = [
    { value: 'general', label: 'Genel Soru', icon: HelpCircle, color: '#6b7280' },
    { value: 'bug', label: 'Hata Bildirimi', icon: Bug, color: '#ef4444' },
    { value: 'feature', label: 'Oneriler', icon: Lightbulb, color: '#f59e0b' },
    { value: 'billing', label: 'Odeme & Abonelik', icon: CreditCard, color: '#8b5cf6' },
];

const PRIORITIES = [
    { value: 'low', label: 'Dusuk', color: '#22c55e' },
    { value: 'medium', label: 'Normal', color: '#f59e0b' },
    { value: 'high', label: 'Yuksek', color: '#ef4444' },
];

const STATUS_MAP = {
    open: { label: 'Acik', color: '#3b82f6', bg: 'rgba(59,130,246,0.1)', icon: AlertCircle },
    in_progress: { label: 'Inceleniyor', color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', icon: Clock },
    resolved: { label: 'Cozuldu', color: '#22c55e', bg: 'rgba(34,197,94,0.1)', icon: CheckCircle2 },
    closed: { label: 'Kapandi', color: '#6b7280', bg: 'rgba(107,114,128,0.1)', icon: CheckCircle2 },
};

const Support = () => {
    const { user, userData, effectiveTenantId } = useAuth();
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('list'); // list | detail | new
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [sendingMessage, setSendingMessage] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [creating, setCreating] = useState(false);
    const [newTicket, setNewTicket] = useState({
        subject: '',
        category: 'general',
        priority: 'medium',
        message: '',
    });
    const messagesEndRef = useRef(null);

    // Fetch tickets
    useEffect(() => {
        if (!effectiveTenantId) return;
        const fetchTickets = async () => {
            setLoading(true);
            const { data } = await supabase
                .from('support_tickets')
                .select('*')
                .eq('tenant_id', effectiveTenantId)
                .order('created_at', { ascending: false });
            setTickets(data || []);
            setLoading(false);
        };
        fetchTickets();

        const channel = supabase
            .channel(`support-tickets-${effectiveTenantId}`)
            .on('postgres_changes', {
                event: '*', schema: 'public', table: 'support_tickets',
                filter: `tenant_id=eq.${effectiveTenantId}`
            }, (payload) => {
                if (payload.eventType === 'INSERT') {
                    setTickets(prev => [payload.new, ...prev]);
                } else if (payload.eventType === 'UPDATE') {
                    setTickets(prev => prev.map(t => t.id === payload.new.id ? payload.new : t));
                    if (selectedTicket?.id === payload.new.id) setSelectedTicket(payload.new);
                } else if (payload.eventType === 'DELETE') {
                    setTickets(prev => prev.filter(t => t.id !== payload.old.id));
                }
            })
            .subscribe();

        return () => supabase.removeChannel(channel);
    }, [effectiveTenantId]);

    // Fetch messages when ticket selected
    useEffect(() => {
        if (!selectedTicket) return;
        const fetchMessages = async () => {
            const { data } = await supabase
                .from('support_messages')
                .select('*')
                .eq('ticket_id', selectedTicket.id)
                .order('created_at', { ascending: true });
            setMessages(data || []);
        };
        fetchMessages();

        const channel = supabase
            .channel(`support-messages-${selectedTicket.id}`)
            .on('postgres_changes', {
                event: 'INSERT', schema: 'public', table: 'support_messages',
                filter: `ticket_id=eq.${selectedTicket.id}`
            }, (payload) => {
                setMessages(prev => [...prev, payload.new]);
            })
            .subscribe();

        return () => supabase.removeChannel(channel);
    }, [selectedTicket?.id]);

    // Auto-scroll messages
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleCreateTicket = async () => {
        if (!newTicket.subject.trim() || !newTicket.message.trim()) return;
        setCreating(true);
        try {
            const { data: ticket, error } = await supabase
                .from('support_tickets')
                .insert({
                    tenant_id: effectiveTenantId,
                    user_id: user.id,
                    user_email: userData?.email || user.email,
                    user_name: userData?.firstName ? `${userData.firstName} ${userData.lastName || ''}`.trim() : null,
                    subject: newTicket.subject,
                    category: newTicket.category,
                    priority: newTicket.priority,
                    status: 'open',
                })
                .select()
                .single();

            if (error) throw error;

            await supabase.from('support_messages').insert({
                ticket_id: ticket.id,
                sender_type: 'tenant',
                sender_id: user.id,
                sender_name: userData?.firstName ? `${userData.firstName} ${userData.lastName || ''}`.trim() : 'Kullanici',
                message: newTicket.message,
            });

            setNewTicket({ subject: '', category: 'general', priority: 'medium', message: '' });
            setSelectedTicket(ticket);
            setView('detail');
        } catch (err) {
            console.error('Error creating ticket:', err);
        }
        setCreating(false);
    };

    const handleSendMessage = async () => {
        if (!newMessage.trim() || !selectedTicket) return;
        setSendingMessage(true);
        try {
            await supabase.from('support_messages').insert({
                ticket_id: selectedTicket.id,
                sender_type: 'tenant',
                sender_id: user.id,
                sender_name: userData?.firstName ? `${userData.firstName} ${userData.lastName || ''}`.trim() : 'Kullanici',
                message: newMessage,
            });

            // Reopen if resolved/closed
            if (selectedTicket.status === 'resolved' || selectedTicket.status === 'closed') {
                await supabase.from('support_tickets')
                    .update({ status: 'open', updated_at: new Date().toISOString() })
                    .eq('id', selectedTicket.id);
            }

            setNewMessage('');
        } catch (err) {
            console.error('Error sending message:', err);
        }
        setSendingMessage(false);
    };

    const filteredTickets = tickets.filter(t => {
        if (filterStatus !== 'all' && t.status !== filterStatus) return false;
        if (searchQuery && !t.subject.toLowerCase().includes(searchQuery.toLowerCase())) return false;
        return true;
    });

    const openCount = tickets.filter(t => t.status === 'open').length;
    const inProgressCount = tickets.filter(t => t.status === 'in_progress').length;
    const resolvedCount = tickets.filter(t => t.status === 'resolved' || t.status === 'closed').length;

    const formatDate = (d) => {
        const date = new Date(d);
        const now = new Date();
        const diffMs = now - date;
        const diffMin = Math.floor(diffMs / 60000);
        if (diffMin < 1) return 'Az once';
        if (diffMin < 60) return `${diffMin} dk once`;
        const diffH = Math.floor(diffMin / 60);
        if (diffH < 24) return `${diffH} saat once`;
        const diffD = Math.floor(diffH / 24);
        if (diffD < 7) return `${diffD} gun once`;
        return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    const formatMessageTime = (d) => {
        const date = new Date(d);
        return date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) +
            ' - ' + date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
    };

    // ==================== NEW TICKET VIEW ====================
    if (view === 'new') {
        return (
            <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '2rem' }}>
                    <button
                        onClick={() => setView('list')}
                        style={{
                            background: 'rgba(0,0,0,0.05)',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '10px',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--text-main)',
                            transition: 'all 0.2s',
                            outline: 'none',
                        }}
                        onMouseOver={e => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}
                        onMouseOut={e => e.currentTarget.style.background = 'rgba(0,0,0,0.05)'}
                    >
                        <X size={20} strokeWidth={2.5} />
                    </button>
                    <div>
                        <h1 style={{ fontWeight: 900, fontSize: '2.25rem', letterSpacing: '-0.02em', margin: 0 }}>
                            Yeni Destek Talebi
                        </h1>
                        <p style={{ color: 'var(--text-muted)', fontSize: '0.9375rem', margin: '4px 0 0' }}>
                            Sorununuzu detayli bir sekilde aciklayın, en kısa surede donecegiz.
                        </p>
                    </div>
                </div>

                <div style={{
                    background: 'var(--bg-card)', border: '1px solid var(--border)',
                    borderRadius: '20px', padding: '2rem', maxWidth: '720px'
                }}>
                    {/* Subject */}
                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Konu
                        </label>
                        <input
                            value={newTicket.subject}
                            onChange={e => setNewTicket(p => ({ ...p, subject: e.target.value }))}
                            placeholder="Sorununuzu kısaca ozetleyin..."
                            style={{
                                width: '100%', padding: '14px 16px', borderRadius: '14px',
                                border: '1px solid var(--border)', background: 'var(--bg-main)',
                                color: 'var(--text-main)', fontSize: '1rem', fontWeight: 500,
                                outline: 'none', transition: 'border-color 0.2s',
                                boxSizing: 'border-box',
                            }}
                            onFocus={e => e.target.style.borderColor = 'var(--primary)'}
                            onBlur={e => e.target.style.borderColor = 'var(--border)'}
                        />
                    </div>

                    {/* Category */}
                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Kategori
                        </label>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '10px' }}>
                            {CATEGORIES.map(cat => {
                                const selected = newTicket.category === cat.value;
                                return (
                                    <button
                                        key={cat.value}
                                        onClick={() => setNewTicket(p => ({ ...p, category: cat.value }))}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: '10px',
                                            padding: '12px 14px', borderRadius: '14px',
                                            border: selected ? `2px solid ${cat.color}` : '1.5px solid var(--border)',
                                            background: selected ? `${cat.color}12` : 'var(--bg-main)',
                                            cursor: 'pointer', transition: 'all 0.2s',
                                            color: selected ? cat.color : 'var(--text-main)',
                                            fontWeight: 600, fontSize: '0.875rem',
                                        }}
                                    >
                                        <cat.icon size={18} />
                                        {cat.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Priority */}
                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Oncelik
                        </label>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            {PRIORITIES.map(p => {
                                const selected = newTicket.priority === p.value;
                                return (
                                    <button
                                        key={p.value}
                                        onClick={() => setNewTicket(prev => ({ ...prev, priority: p.value }))}
                                        style={{
                                            padding: '10px 20px', borderRadius: '12px',
                                            border: selected ? `2px solid ${p.color}` : '1.5px solid var(--border)',
                                            background: selected ? `${p.color}15` : 'var(--bg-main)',
                                            cursor: 'pointer', transition: 'all 0.2s',
                                            color: selected ? p.color : 'var(--text-muted)',
                                            fontWeight: 600, fontSize: '0.875rem',
                                        }}
                                    >
                                        <span style={{
                                            display: 'inline-block', width: '8px', height: '8px',
                                            borderRadius: '50%', background: p.color, marginRight: '8px',
                                        }} />
                                        {p.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Message */}
                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Mesajınız
                        </label>
                        <textarea
                            value={newTicket.message}
                            onChange={e => setNewTicket(p => ({ ...p, message: e.target.value }))}
                            placeholder="Sorununuzu detayli bir sekilde aciklayın..."
                            rows={6}
                            style={{
                                width: '100%', padding: '14px 16px', borderRadius: '14px',
                                border: '1px solid var(--border)', background: 'var(--bg-main)',
                                color: 'var(--text-main)', fontSize: '0.9375rem', fontWeight: 500,
                                outline: 'none', resize: 'vertical', fontFamily: 'inherit',
                                lineHeight: 1.6, transition: 'border-color 0.2s',
                                boxSizing: 'border-box',
                            }}
                            onFocus={e => e.target.style.borderColor = 'var(--primary)'}
                            onBlur={e => e.target.style.borderColor = 'var(--border)'}
                        />
                    </div>

                    {/* Submit */}
                    <button
                        onClick={handleCreateTicket}
                        disabled={creating || !newTicket.subject.trim() || !newTicket.message.trim()}
                        style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                            width: '100%', padding: '16px', borderRadius: '16px',
                            background: (!newTicket.subject.trim() || !newTicket.message.trim()) ? 'var(--border)' : 'var(--primary)',
                            color: (!newTicket.subject.trim() || !newTicket.message.trim()) ? 'var(--text-muted)' : 'var(--text-on-primary)',
                            border: 'none', fontWeight: 700, fontSize: '1rem',
                            cursor: (!newTicket.subject.trim() || !newTicket.message.trim()) ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s',
                            opacity: creating ? 0.7 : 1,
                        }}
                    >
                        {creating ? <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={18} />}
                        {creating ? 'Gonderiliyor...' : 'Talep Olustur'}
                    </button>
                </div>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    // ==================== DETAIL VIEW ====================
    if (view === 'detail' && selectedTicket) {
        const statusInfo = STATUS_MAP[selectedTicket.status] || STATUS_MAP.open;
        const catInfo = CATEGORIES.find(c => c.value === selectedTicket.category) || CATEGORIES[0];
        const prioInfo = PRIORITIES.find(p => p.value === selectedTicket.priority) || PRIORITIES[1];

        return (
            <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '1.5rem' }}>
                    <button
                        onClick={() => { setView('list'); setSelectedTicket(null); }}
                        style={{
                            background: 'rgba(0,0,0,0.05)',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '10px',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--text-main)',
                            transition: 'all 0.2s',
                            outline: 'none',
                        }}
                        onMouseOver={e => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}
                        onMouseOut={e => e.currentTarget.style.background = 'rgba(0,0,0,0.05)'}
                    >
                        <X size={20} strokeWidth={2.5} />
                    </button>
                    <div style={{ flex: 1 }}>
                        <h1 style={{ fontWeight: 900, fontSize: '1.75rem', letterSpacing: '-0.02em', margin: 0 }}>
                            {selectedTicket.subject}
                        </h1>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '6px', flexWrap: 'wrap' }}>
                            <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: '6px',
                                padding: '4px 12px', borderRadius: '20px', fontSize: '0.75rem',
                                fontWeight: 700, background: statusInfo.bg, color: statusInfo.color,
                            }}>
                                <statusInfo.icon size={12} /> {statusInfo.label}
                            </span>
                            <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: '6px',
                                padding: '4px 12px', borderRadius: '20px', fontSize: '0.75rem',
                                fontWeight: 600, background: `${catInfo.color}15`, color: catInfo.color,
                            }}>
                                <catInfo.icon size={12} /> {catInfo.label}
                            </span>
                            <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                                {formatDate(selectedTicket.created_at)}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Messages */}
                <div style={{
                    background: 'var(--bg-card)', border: '1px solid var(--border)',
                    borderRadius: '20px', overflow: 'hidden', display: 'flex',
                    flexDirection: 'column', height: 'calc(100vh - 280px)', minHeight: '400px',
                }}>
                    {/* Message list */}
                    <div style={{
                        flex: 1, overflowY: 'auto', padding: '1.5rem',
                        display: 'flex', flexDirection: 'column', gap: '16px',
                    }}>
                        {messages.length === 0 && (
                            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                                <MessageSquare size={40} style={{ opacity: 0.3, marginBottom: '12px' }} />
                                <p style={{ fontSize: '0.9375rem' }}>Henuz mesaj yok</p>
                            </div>
                        )}
                        {messages.map(msg => {
                            const isAdmin = msg.sender_type === 'admin';
                            return (
                                <div
                                    key={msg.id}
                                    style={{
                                        display: 'flex',
                                        justifyContent: isAdmin ? 'flex-start' : 'flex-end',
                                    }}
                                >
                                    <div style={{
                                        maxWidth: '75%', padding: '14px 18px',
                                        borderRadius: isAdmin ? '18px 18px 18px 4px' : '18px 18px 4px 18px',
                                        background: isAdmin ? 'var(--bg-main)' : 'var(--primary)',
                                        color: isAdmin ? 'var(--text-main)' : 'var(--text-on-primary)',
                                        border: isAdmin ? '1px solid var(--border)' : 'none',
                                    }}>
                                        <div style={{
                                            fontSize: '0.75rem', fontWeight: 700, marginBottom: '6px',
                                            opacity: isAdmin ? 1 : 0.85,
                                            color: isAdmin ? 'var(--primary)' : 'rgba(255,255,255,0.85)',
                                        }}>
                                            {isAdmin ? 'Ugra Destek' : (msg.sender_name || 'Siz')}
                                        </div>
                                        <p style={{
                                            margin: 0, fontSize: '0.9375rem', lineHeight: 1.6,
                                            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                                        }}>
                                            {msg.message}
                                        </p>
                                        <div style={{
                                            fontSize: '0.6875rem', marginTop: '8px',
                                            opacity: 0.6, textAlign: 'right',
                                        }}>
                                            {formatMessageTime(msg.created_at)}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input bar */}
                    <div style={{
                        padding: '16px 20px', borderTop: '1px solid var(--border)',
                        display: 'flex', alignItems: 'center', gap: '12px',
                        background: 'var(--bg-card)',
                    }}>
                        <input
                            value={newMessage}
                            onChange={e => setNewMessage(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                            placeholder="Mesajınızı yazın..."
                            style={{
                                flex: 1, padding: '14px 18px', borderRadius: '16px',
                                border: '1px solid var(--border)', background: 'var(--bg-main)',
                                color: 'var(--text-main)', fontSize: '0.9375rem',
                                outline: 'none', transition: 'border-color 0.2s',
                            }}
                            onFocus={e => e.target.style.borderColor = 'var(--primary)'}
                            onBlur={e => e.target.style.borderColor = 'var(--border)'}
                        />
                        <button
                            onClick={handleSendMessage}
                            disabled={!newMessage.trim() || sendingMessage}
                            style={{
                                width: '48px', height: '48px', borderRadius: '14px',
                                border: 'none', background: newMessage.trim() ? 'var(--primary)' : 'var(--border)',
                                color: newMessage.trim() ? 'var(--text-on-primary)' : 'var(--text-muted)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: newMessage.trim() ? 'pointer' : 'not-allowed',
                                transition: 'all 0.2s', flexShrink: 0,
                            }}
                        >
                            {sendingMessage ? <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={20} />}
                        </button>
                    </div>
                </div>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    // ==================== LIST VIEW ====================
    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
                <div>
                    <h1 style={{ fontWeight: 900, fontSize: '2.25rem', letterSpacing: '-0.02em', margin: 0 }}>
                        Destek
                    </h1>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.9375rem', margin: '4px 0 0' }}>
                        Sorularınız ve sorunlarınız icin bize ulasın.
                    </p>
                </div>
            </div>

            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '1.5rem' }}>
                {[
                    { label: 'Acik', count: openCount, color: '#3b82f6', icon: AlertCircle },
                    { label: 'Inceleniyor', count: inProgressCount, color: '#f59e0b', icon: Clock },
                    { label: 'Cozuldu', count: resolvedCount, color: '#22c55e', icon: CheckCircle2 },
                    { label: 'Toplam', count: tickets.length, color: 'var(--primary)', icon: MessageSquare },
                ].map(stat => (
                    <div key={stat.label} style={{
                        background: 'var(--bg-card)', border: '1px solid var(--border)',
                        borderRadius: '16px', padding: '16px 20px',
                        display: 'flex', alignItems: 'center', gap: '14px',
                    }}>
                        <div style={{
                            width: '42px', height: '42px', borderRadius: '12px',
                            background: `${stat.color}15`, display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                        }}>
                            <stat.icon size={20} color={stat.color} />
                        </div>
                        <div>
                            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text-main)', lineHeight: 1 }}>
                                {stat.count}
                            </div>
                            <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                                {stat.label}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Search & Filter */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
                    <Search size={16} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                    <input
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        placeholder="Taleplerde ara..."
                        style={{
                            width: '100%', padding: '12px 14px 12px 40px', borderRadius: '14px',
                            border: '1px solid var(--border)', background: 'var(--bg-card)',
                            color: 'var(--text-main)', fontSize: '0.9375rem', outline: 'none',
                            boxSizing: 'border-box',
                        }}
                    />
                </div>

                <button
                    onClick={() => setView('new')}
                    style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '0 20px', height: '46px', borderRadius: '14px',
                        background: 'var(--primary)', color: 'var(--text-on-primary)',
                        border: 'none', fontWeight: 700, fontSize: '0.9rem',
                        cursor: 'pointer', transition: 'all 0.2s',
                        boxShadow: '0 4px 12px rgba(var(--primary-rgb), 0.2)',
                        whiteSpace: 'nowrap'
                    }}
                >
                    <Plus size={18} />
                    Yeni Talep
                </button>
                <div style={{ display: 'flex', gap: '6px' }}>
                    {['all', 'open', 'in_progress', 'resolved'].map(s => (
                        <button
                            key={s}
                            onClick={() => setFilterStatus(s)}
                            style={{
                                padding: '10px 16px', borderRadius: '12px',
                                border: filterStatus === s ? '2px solid var(--primary)' : '1.5px solid var(--border)',
                                background: filterStatus === s ? 'rgba(var(--primary-rgb), 0.08)' : 'var(--bg-card)',
                                color: filterStatus === s ? 'var(--primary)' : 'var(--text-muted)',
                                fontWeight: 600, fontSize: '0.8125rem',
                                cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap',
                            }}
                        >
                            {s === 'all' ? 'Tumu' : STATUS_MAP[s]?.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Ticket list */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-muted)' }}>
                    <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', marginBottom: '12px' }} />
                    <p>Yukleniyor...</p>
                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                </div>
            ) : filteredTickets.length === 0 ? (
                <div style={{
                    textAlign: 'center', padding: '4rem 2rem',
                    background: 'var(--bg-card)', border: '1px solid var(--border)',
                    borderRadius: '20px',
                }}>
                    <div style={{
                        width: '64px', height: '64px', borderRadius: '20px',
                        background: 'rgba(var(--primary-rgb), 0.08)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        margin: '0 auto 1rem',
                    }}>
                        <LifeBuoy size={28} color="var(--primary)" />
                    </div>
                    <h3 style={{ fontWeight: 700, marginBottom: '8px', color: 'var(--text-main)' }}>
                        {tickets.length === 0 ? 'Henuz destek talebiniz yok' : 'Sonuc bulunamadı'}
                    </h3>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.9375rem', marginBottom: '1.5rem' }}>
                        {tickets.length === 0
                            ? 'Bir sorunuz veya sorununuz mu var? Hemen bize ulasın!'
                            : 'Arama veya filtre kriterlerinizi degistirin.'}
                    </p>
                    {tickets.length === 0 && (
                        <button
                            onClick={() => setView('new')}
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: '8px',
                                padding: '12px 24px', borderRadius: '14px',
                                background: 'var(--primary)', color: 'var(--text-on-primary)',
                                border: 'none', fontWeight: 700, cursor: 'pointer',
                            }}
                        >
                            <Plus size={18} /> Ilk Talebinizi Olusturun
                        </button>
                    )}
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {filteredTickets.map(ticket => {
                        const statusInfo = STATUS_MAP[ticket.status] || STATUS_MAP.open;
                        const catInfo = CATEGORIES.find(c => c.value === ticket.category) || CATEGORIES[0];
                        return (
                            <div
                                key={ticket.id}
                                onClick={() => { setSelectedTicket(ticket); setView('detail'); }}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '16px',
                                    padding: '18px 20px', borderRadius: '16px',
                                    background: 'var(--bg-card)', border: '1px solid var(--border)',
                                    cursor: 'pointer', transition: 'all 0.2s',
                                }}
                                onMouseEnter={e => {
                                    e.currentTarget.style.borderColor = 'var(--primary)';
                                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.06)';
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                }}
                                onMouseLeave={e => {
                                    e.currentTarget.style.borderColor = 'var(--border)';
                                    e.currentTarget.style.boxShadow = 'none';
                                    e.currentTarget.style.transform = 'none';
                                }}
                            >
                                <div style={{
                                    width: '44px', height: '44px', borderRadius: '14px',
                                    background: `${catInfo.color}12`, display: 'flex',
                                    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                }}>
                                    <catInfo.icon size={20} color={catInfo.color} />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{
                                        fontWeight: 700, fontSize: '0.9375rem',
                                        color: 'var(--text-main)',
                                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                    }}>
                                        {ticket.subject}
                                    </div>
                                    <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginTop: '4px' }}>
                                        {catInfo.label} &middot; {formatDate(ticket.created_at)}
                                    </div>
                                </div>
                                <span style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                                    padding: '6px 14px', borderRadius: '20px', fontSize: '0.75rem',
                                    fontWeight: 700, background: statusInfo.bg, color: statusInfo.color,
                                    whiteSpace: 'nowrap', flexShrink: 0,
                                }}>
                                    <statusInfo.icon size={12} /> {statusInfo.label}
                                </span>
                                <ChevronRight size={18} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default Support;
